import { escapeHtml } from '../../utils/html'

interface ReleaseTemplateInput {
	releaseId: number
	releaseInstanceId: number
	headerClassName: string
	title: string
	artists: string
	year: string
	coverSrc: string
	coverPlaceholderSrc: string
	headerRatingHtml: string
	ratingEditorHtml: string
	editRatingLabel: string
	listenButtonText: string
	editButtonText: string
	favoriteButtonLabel: string
	isFavorite: boolean
	releaseInfosLabel: string
	tracklistLabel: string
	historyHtml: string
	releaseInfoHtml: string
	tracklistHtml: string
	loadingLabel: string
	retryLabel: string
	loadLabel: string
	readOnly?: boolean
}

interface ReleaseLazyPreview {
	artists: string
	coverPlaceholderSrc: string
	coverSrc: string
	headerClassName: string
	headerRatingHtml: string
	title: string
	year: string
}

type ReleaseHeroTemplateInput = ReleaseLazyPreview & {
	coverExtraHtml?: string
	lazyLoadCover?: boolean
}

export function releaseYearLoadingTemplate(label: string): string {
	return `<span class="is-loading" aria-busy="true" aria-label="${escapeHtml(label)}"><span class="loading-placeholder" aria-hidden="true">${escapeHtml(label)}</span></span>`
}

export function releaseLazyTemplate(
	releaseId: number,
	releaseInstanceId?: number,
	preview?: ReleaseLazyPreview,
): string {
	const instanceAttr =
		releaseInstanceId === undefined
			? ''
			: ` data-release-instance-id="${releaseInstanceId}"`
	if (!preview) {
		return `<div id="release" class="release-loading" data-release-id="${releaseId}"${instanceAttr}></div>`
	}

	return `
		<div id="release" class="release-loading" data-release-id="${releaseId}"${instanceAttr}>
			${releaseHeroTemplate({
				...preview,
				year: escapeHtml(preview.year),
			})}
		</div>
	`
}

export function releaseTemplate({
	releaseId,
	releaseInstanceId,
	headerClassName,
	title,
	artists,
	year,
	coverSrc,
	coverPlaceholderSrc,
	headerRatingHtml,
	ratingEditorHtml,
	editRatingLabel,
	listenButtonText,
	editButtonText,
	favoriteButtonLabel,
	isFavorite,
	releaseInfosLabel,
	tracklistLabel,
	historyHtml,
	releaseInfoHtml,
	tracklistHtml,
	loadingLabel,
	retryLabel,
	loadLabel,
	readOnly = false,
}: ReleaseTemplateInput): string {
	return `
        <div
            id="release"
	            data-release-id="${releaseId}"
	            data-release-instance-id="${releaseInstanceId}"
			data-read-only="${String(readOnly)}"
        >
			${releaseHeroTemplate({
				artists,
				coverPlaceholderSrc,
				coverSrc,
				headerClassName,
				headerRatingHtml,
				lazyLoadCover: true,
				title,
				year,
				coverExtraHtml: readOnly
					? ''
					: `<button
					type="button"
					class="release-rating-toggle"
					data-release-rating-toggle
					aria-controls="release-rating-editor"
					aria-expanded="false"
					aria-label="${editRatingLabel}"
				></button>${ratingEditorHtml}`,
			})}

	            ${
					readOnly
						? ''
						: `<div id="release-actions">
				<button
					id="favorite-button"
					class="secondary icon${isFavorite ? ' is-favorite' : ''}"
					type="button"
					aria-label="${favoriteButtonLabel}"
					aria-pressed="${isFavorite}"
					title="${favoriteButtonLabel}"
				>
					<svg aria-hidden="true"><use href="#symbols-heart"></use></svg>
				</button>
                <button id="listen-button" class="primary">
                    <svg><use href="#symbols-check-circle"></use></svg>
                    ${listenButtonText}
                </button>
				<button
					id="edit-button"
					class="secondary icon"
					type="button"
					aria-label="${editButtonText}"
					title="${editButtonText}"
				>
                    <svg><use href="#symbols-pencil"></use></svg>
                </button>
				</div>`
				}

			<div id="release-tab-area" class="tab-menu-container">
                <div class="tab-list" role="tablist" data-active-tab="history">
                    <button
                        id="release-history-btn"
                        class="btn noFrame tab-button"
                        aria-expanded="true"
                        aria-selected="true"
                        data-tab="history"
                    >
                        ${releaseInfosLabel}
                    </button>
                    <button
                        id="release-infos-btn"
                        class="btn noFrame tab-button"
                        aria-expanded="false"
                        aria-selected="false"
                        data-tab="details"
                    >
                        ${tracklistLabel}
                    </button>
                    <span class="tab-indicator" aria-hidden="true"></span>
                </div>

                <div id="release-history-container" class="tab-panel active" data-tab-panel="history">
                    <ul id="release-history-list">
	                        ${readOnly ? '' : historyHtml}
                    </ul>
                    <ul id="release-infos" data-release-id="${releaseId}">
                        ${releaseInfoHtml}
                    </ul>
                </div>
                <div id="release-infos-container" class="tab-panel" data-tab-panel="details">
                    ${tracklistHtml ? '<p class="f secondary">Preview availability is checked automatically through Apple. Playing a preview connects your device to the audio provider.</p>' : ''}
                    <ul id="release-tracklist">${tracklistHtml}</ul>
                    <div class="detail-status-container status-ok"></div>
                    <div class="detail-status-container status-loading">
                        ${loadingLabel}
                    </div>
                    <div class="detail-status-container status-error">
                        <button>${retryLabel}</button>
                    </div>
                    <div class="detail-status-container status-none">
                        <button>${loadLabel}</button>
                    </div>
                </div>
            </div>

            <div class="release-similar-items" data-release-similar-items></div>
        </div>
    `
}

function releaseHeroTemplate({
	artists,
	coverExtraHtml = '',
	coverPlaceholderSrc,
	coverSrc,
	headerClassName,
	headerRatingHtml,
	lazyLoadCover = false,
	title,
	year,
}: ReleaseHeroTemplateInput): string {
	// HTML escaping alone cannot protect a URL nested inside a CSS string.
	const cssCoverSrc = coverSrc.replace(
		/[\\'"()\n\r\f]/g,
		(character) =>
			`%${character.charCodeAt(0).toString(16).padStart(2, '0')}`,
	)
	return `
		<div id="release-header" class="${escapeHtml(headerClassName)}">
			<h2>
				<span class="title-text">${escapeHtml(title)}</span>
				<span class="hide-icon-header"><svg><use href="#symbols-eye-closed"></use></svg></span>
			</h2>
			<h3>${escapeHtml(artists)}</h3>
			<h3 class="f secondary release-meta">
				<span data-release-year>${year}</span>
				<span data-release-header-rating${headerRatingHtml ? '' : ' hidden'}>${headerRatingHtml}</span>
			</h3>
		</div>
		<div class="cover-wrapper" style="--cover-url: url('${escapeHtml(cssCoverSrc)}');">
			<img alt="cover" src="${escapeHtml(coverSrc)}"${lazyLoadCover ? ' loading="lazy"' : ''} draggable="false" data-fallback-src="${escapeHtml(coverPlaceholderSrc)}" onerror="this.onerror=null;this.src=this.dataset.fallbackSrc" />
			${coverExtraHtml}
		</div>
	`
}
